import {
  IconCpu,
  IconLanguage,
  IconLayoutDashboard,
  IconListDetails,
  IconMailbox,
  IconMasksTheater,
  IconNews,
  IconRoute,
  IconTags,
  IconTargetArrow,
  IconTrendingDown,
  IconUsers,
  IconWaveSine,
  type TablerIcon,
} from '@tabler/icons-react';

export interface NavItem {
  to: string;
  label: string;
  icon: TablerIcon;
  /**
   * Only active on an exact path match.
   *
   * Needed where one entry's path is a prefix of another's: /analytics would
   * otherwise light up for every screen in the section.
   */
  exact?: boolean;
  /** Small marker beside the label. Used to mark screens showing invented data. */
  badge?: string;
}

export interface NavSection {
  /** Rendered as a heading above the group. */
  title: string;
  items: NavItem[];
}

/**
 * Sidebar contents. Add screens here as they are built.
 *
 * Sections exist from the first entry so that adding the second and third
 * screens is one line each rather than a restructure.
 *
 * The Analytics screens still on invented data carry a "Sample" badge, because
 * the banner on each of them does not survive a cropped screenshot. Funnel has
 * none: it reads GET /v1/admin/analytics/funnel and every number on it is
 * measured.
 */
export const navSections: NavSection[] = [
  {
    title: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: IconLayoutDashboard }],
  },
  {
    title: 'Analytics',
    items: [
      {
        to: '/analytics',
        label: 'Funnel',
        icon: IconListDetails,
        exact: true,
      },
      {
        to: '/analytics/drop-off',
        label: 'Drop-off',
        icon: IconTrendingDown,
        badge: 'Sample',
      },
      {
        to: '/analytics/signals',
        label: 'Signals',
        icon: IconWaveSine,
        badge: 'Sample',
      },
      {
        to: '/analytics/journeys',
        label: 'Journeys',
        icon: IconRoute,
        badge: 'Sample',
      },
    ],
  },
  {
    title: 'People',
    // No `exact`: isNavItemActive already matches a child path, and this entry
    // is the case its doc comment was written for.
    items: [{ to: '/users', label: 'Users', icon: IconUsers }],
  },
  {
    // Its own section rather than a second item under People, because these are
    // not users: nobody on this list has a `users` row, a session or an account,
    // and filing them under People would imply a relationship that does not
    // exist. The section is the category and the item is the screen, matching
    // People -> Users; a section and an item both called "Waitlist" would read
    // as a bug.
    title: 'Waitlist',
    items: [{ to: '/waitlist', label: 'Signups', icon: IconMailbox }],
  },
  {
    title: 'Settings',
    items: [
      {
        to: '/settings/languages',
        label: 'Languages',
        icon: IconLanguage,
      },
      {
        to: '/settings/motivations',
        label: 'Reasons',
        icon: IconTargetArrow,
      },
      {
        to: '/settings/headlines',
        label: 'Headlines',
        icon: IconNews,
      },
      {
        to: '/settings/interests',
        label: 'Interests',
        icon: IconTags,
      },
      {
        to: '/settings/characters',
        label: 'Cast',
        icon: IconMasksTheater,
      },
      {
        to: '/settings/llm',
        label: 'Language model',
        icon: IconCpu,
      },
    ],
  },
];

/**
 * Whether a nav entry should read as active for the current path.
 *
 * A plain equality check was enough while every screen was a top-level route,
 * but it goes quietly wrong the first time one has a child path: `/users/42`
 * would leave "Users" unhighlighted. The index route has to stay an exact
 * match, since every path starts with `/`, and so does any entry whose path is
 * a prefix of a sibling's.
 */
export function isNavItemActive(pathname: string, item: NavItem): boolean {
  if (item.to === '/' || item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
